import moment from "moment";

import { Client } from "@/Common/Api/Client";
import { SimpleDisplayModel } from "@/Common/Components/Search/SimpleSearchAutocomplete";
import { EnumHelper } from "@/Common/Helpers/EnumHelper";
import { IMap } from "@/Common/Models/IMap";
import { KeyValuePair } from "@/Common/Models/UI/KeyValuePair";
import * as ServerOperations from "@/Common/Operations/operations.server";
import { MedicalItemImmunization } from "@/Patient/Models/MedicalSummary/MedicalItemImmunization";
import { ConstantValues } from "./Constants/ConstantValues";
import { ImmunizationStatus } from "./Constants/ImmunizationStatus";
import { ValueSetType } from "./Constants/ValueSetType";
import { CodeValueSearchStrategy } from "./ImmunizationProfileSections/CodeValueSearchAutocompleteCustom";
import { BaseRvqPreference } from "./Models/BaseRvqPreference";
import { CodeValue } from "./Models/CodeValue";
import { ImmunizationModel } from "./Models/ImmunizationModel";
import { MedicationBatchModel } from "./Models/MedicalBatchModel";
import { RvqDomainValue } from "./Models/RvqDomainValue";
import { RvqLocationPreference } from "./Models/RvqLocationPreference";
import { RvqMedicationPreference } from "./Models/RvqMedicationPreference";
import { RvqPractitionerPreference } from "./Models/RvqPractitionerPreference";
import { GetRvqMedicationByTradeNameResponse } from "./ResponseModels/GetRvqMedicationByTradeNameRequest";
import { GetValuesResponse } from "./ResponseModels/GetValuesResponse";

class QuebecVaccinationRegistryHelper {
    public static getCodeValues(values: KeyValuePair<string, string>[]): CodeValue[] {
        return (values || [])
            .map(x => new CodeValue({
                Label: x.Value,
                Code: x.Key
            }));
    }

    public static filterCodeValues(codeValues: CodeValue[], codes: string[]): CodeValue[] {
        if (!codeValues?.any())
            return null;

        return codes
            .map(antigen => codeValues.firstOrDefault(x => x.Code.trim() === antigen.trim()));
    }

    public static getDefaultPreference<TPreference extends BaseRvqPreference>(preferences: TPreference[]): TPreference {
        let defaultPreference = preferences.firstOrDefault(x => x.IsPinned);

        if (!defaultPreference)
            defaultPreference = preferences
                .filter(x => x.LastAppliedDate && moment(x.LastAppliedDate).isValid())
                .sortByPropertyDesc(x => moment(x.LastAppliedDate))
                .firstOrDefault();

        return defaultPreference;
    }

    public static getDefaultPreferences(medicationPreference: RvqMedicationPreference[] = [], practitionerPreference: RvqPractitionerPreference[] = [], locationPreference: RvqLocationPreference[] = []): { defaultMedication: RvqMedicationPreference, defaultPractitioner: RvqPractitionerPreference, defaultLocation: RvqLocationPreference } {
        const defaultLocation = this.getDefaultPreference(locationPreference);
        const defaultPractitioner = this.getDefaultPreference(practitionerPreference);
        const defaultMedication = this.getDefaultPreference(medicationPreference);

        return {
            defaultMedication,
            defaultPractitioner,
            defaultLocation
        };
    }

    public static sortBasePreferenceArray<TPreference extends BaseRvqPreference>(preferences: TPreference[]): TPreference[] {
        return preferences
            ?.sortByProperty(x => cacheStore.Helper.RemoveDiacritics(x.DisplayName)?.toLowerCase())
            .sortByProperty(x => x.IsFavorite, true)
            .sortByProperty(x => x.IsPinned, true);
    }

    public static getSortedPreferenceList<TPreference extends BaseRvqPreference>(preferences: TPreference[]): SimpleDisplayModel<TPreference>[] {
        return this
            .sortBasePreferenceArray(preferences || [])
            .map((pref, index) => new SimpleDisplayModel<TPreference>({
                Item: {
                    Id: pref.comparableRvqId,
                    DisplayText: pref.DisplayName,
                    Data: pref
                },
                Active: index === 0
            }));
    }

    public static isQuebecRegistryVaccinationSynced(immunization: MedicalItemImmunization): boolean {
        let isDataSyncedWithRvq = !!immunization.SubmittedToQuebecRegistryOn;
        if (immunization.IsRefused) {
            return isDataSyncedWithRvq;
        }
        else {
            isDataSyncedWithRvq = isDataSyncedWithRvq && immunization.QuebecRegistryId && moment(immunization.SubmittedToQuebecRegistryOn).isAfter(immunization.LastUpdateDate ?? immunization.CreationDate);
            const isInvalidated = immunization.DisplayAsInvalid;

            if (isInvalidated)
                return !immunization.QuebecRegistryId && immunization.QuebecRegistryDeletionDate && moment(immunization.QuebecRegistryDeletionDate).isValid();
            else
                return isDataSyncedWithRvq;
        }
    }

    public static getQuebecRegistryVaccinationSyncDate(immunization: MedicalItemImmunization): string {
        if (this.isQuebecRegistryVaccinationSynced(immunization))
            return moment(
                immunization.DisplayAsInvalid
                    ? immunization.QuebecRegistryDeletionDate
                    : immunization.SubmittedToQuebecRegistryOn
            )
                .format("YYYY-MM-DD hh:mm:ss");
        else
            return null;
    }

    public static isResetStatusAction(overrideStatus: CodeValue): boolean {
        switch (overrideStatus?.Code) {
            case ImmunizationStatus.OverriddenToInvalid:
            case ImmunizationStatus.OverriddenToValid:
                return true;

            default:
                return false;
        }
    }

    public static translateValueSetString(codeValue: CodeValue, valueSetType: ValueSetType | string): string {
        return this.translateValueSet(codeValue?.Code, codeValue?.Label, valueSetType);
    }

    public static translateValueSet(code: string, defaultLabel: string, valueSetType: ValueSetType | string): string {
        if (!code)
            return defaultLabel ?? "";

        const typeLabel = EnumHelper.getName(ValueSetType, valueSetType);
        const key = `QuebecVaccinationRegistry_Values_${typeLabel}_${code}`;

        return cacheStore.HasString(key)
            ? cacheStore.GetString(key)
            : defaultLabel;
    }

    public static getTranslatedDictionary(values: IMap<RvqDomainValue>, valueSetType: ValueSetType | string): IMap<string> {
        const result: IMap<string> = {};

        for (let key of Object.keys(values))
            result[key] = this.translateValueSet(key, values[key]?.Label, valueSetType);

        return result;
    }

    public static canDeleteImmunization(immunization: ImmunizationModel, deletePermissions: number[], isCorrector: boolean): boolean {
        return isCorrector || deletePermissions?.any(id => id === immunization.Id);
    }

    public static getImmunizationDisplayName(immunization: MedicalItemImmunization): string {
        let displayName = immunization.Name;
        if (!displayName)
            displayName = this.translateValueSet(immunization.QuebecRegistryAgentCode, immunization.Type, ValueSetType.Agent);

        return immunization.IsQuebecRegistryAllAntigensSelected && immunization.IsRefused
            ? cacheStore.GetString("QuebecVaccinationRegistry_Refusal_AllVaccines")
            : displayName;
    }

    public static async getAntigensByAgentCode(agentCode: string): Promise<CodeValue[]> {
        const response = await Client.postAsync<GetValuesResponse>(
            ServerOperations.Server.QuebecVaccinationRegistry_GetValues,
            {
                AgentCode: agentCode
            }
        );

        return Object
            .keys(response.data?.Antigens)
            .map(key => new CodeValue({ Code: key, Label: response.data.Antigens[key]?.Label }));
    }

    public static convertMyleSiteToRvqSite(siteLookUpId: string): string | null {
        const currentSite = cacheStore.FindLookupsByTypeCode("SITES")?.firstOrDefault(x => x.LookupID === siteLookUpId);

        if (!currentSite)
            return null;

        return ConstantValues.SitesToQuebecVaccinationRegistryConversionCode?.[currentSite.LookupCode];
    }

    public static convertMyleRouteToRvqRoute(routeCode: string): string | null {
        return ConstantValues.RoutesQuebecVaccinationRegistryConversionCode?.[routeCode];
    }

    public static convertMyleDoseUnitToRvqDoseUnits(doseUnitLookUpId: string): string | null {
        const currentDoseUnit = cacheStore.FindLookupsByTypeCode("DosageUnit")?.firstOrDefault(x => x.LookupID === doseUnitLookUpId);

        if (!currentDoseUnit)
            return null;

        return ConstantValues.DoseUnitsQuebecVaccinationRegistryConversionCode?.[currentDoseUnit.LookupCode];
    }

    public static async getBatchesForMedication(tradeName: string): Promise<{ batches: MedicationBatchModel[]; issues: string[] }> {
        let batches: MedicationBatchModel[] = [];
        let issues: string[] = [];

        if (!!tradeName) {
            const medicationResponse = await Client.postAsync<GetRvqMedicationByTradeNameResponse>(
                ServerOperations.Server.QuebecVaccinationRegistry_GetMedicationByTradeName,
                {
                    TradeName: tradeName
                }
            );

            if (medicationResponse.errors.any())
                sessionStore.showPopup(medicationResponse.errors);

            batches = medicationResponse?.data?.Medication?.Batches
                .sortByProperty(x => x.LotNumber.toLowerCase()) ?? [];

            if (medicationResponse.data?.Issues?.any())
                issues = medicationResponse.data.Issues;
        }
        return { batches, issues };
    }

    public static getCodeValueSearchStrategy(codeValues: CodeValue[], costumId: string, valueSetType: ValueSetType): CodeValueSearchStrategy {
        return new CodeValueSearchStrategy(
            codeValues.map(codeValue => new CodeValue({ ...codeValue, Label: QuebecVaccinationRegistryHelper.translateValueSetString(codeValue, valueSetType) })),
            costumId,
            true
        );
    }
}

export { QuebecVaccinationRegistryHelper };
